export interface TrackingInfo {
  tracking_number: string;
  carrier: string | null;
  carrier_label: string | null;
  url: string | null;
  fallback_url: string | null;
}

function normalizeTrackingNumber(value: string): string {
  return String(value || '').replace(/[\s-]/g, '').toUpperCase();
}

type CarrierPattern = {
  carrier: string;
  label: string;
  test: (n: string) => boolean;
  url: (n: string) => string;
};

const CARRIER_PATTERNS: CarrierPattern[] = [
  {
    carrier: 'ups',
    label: 'UPS',
    test: (n) => /^1Z[0-9A-Z]{16}$/i.test(n),
    url: (n) => `https://www.ups.com/track?tracknum=${encodeURIComponent(n)}`,
  },
  {
    carrier: 'fedex',
    label: 'FedEx',
    test: (n) =>
      /^(\d{12}|\d{15}|\d{20}|\d{22})$/.test(n) ||
      /^7489\d{8}$/.test(n) ||
      /^96\d{20}$/.test(n),
    url: (n) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}`,
  },
  {
    carrier: 'usps',
    label: 'USPS',
    test: (n) =>
      /^(94|93|92|95|91|90)\d{20}$/.test(n) ||
      /^(94|93|92|95|91|90)\d{22}$/.test(n) ||
      /^[A-Z]{2}\d{9}[A-Z]{2}$/i.test(n) ||
      /^82\d{8}$/.test(n) ||
      /^\d{26}$/.test(n) ||
      /^\d{30}$/.test(n),
    url: (n) =>
      `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(n)}`,
  },
  {
    carrier: 'dhl',
    label: 'DHL',
    test: (n) =>
      /^\d{10,11}$/.test(n) ||
      /^JD\d{18}$/i.test(n),
    url: (n) =>
      `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${encodeURIComponent(n)}`,
  },
  {
    carrier: 'ontrac',
    label: 'OnTrac',
    test: (n) => /^[CD]\d{14}$/i.test(n),
    url: (n) => `https://www.ontrac.com/tracking/?number=${encodeURIComponent(n)}`,
  },
];

export function getTrackingInfo(trackingNumber?: string | null): TrackingInfo {
  const raw = String(trackingNumber || '').trim();
  const normalized = normalizeTrackingNumber(raw);
  if (!normalized) {
    return {
      tracking_number: '',
      carrier: null,
      carrier_label: null,
      url: null,
      fallback_url: null,
    };
  }

  const fallback_url = `https://www.google.com/search?q=${encodeURIComponent(`${raw} tracking`)}`;

  for (const pattern of CARRIER_PATTERNS) {
    if (pattern.test(normalized)) {
      return {
        tracking_number: raw,
        carrier: pattern.carrier,
        carrier_label: pattern.label,
        url: pattern.url(normalized),
        fallback_url,
      };
    }
  }

  return {
    tracking_number: raw,
    carrier: null,
    carrier_label: null,
    url: null,
    fallback_url,
  };
}
